import React, { useEffect, useRef, useState } from "react";
import { isAuthenticated, getStoredAccessToken } from "../../../utils/authSession";
import { isDesktopApp } from "../../../utils/isDesktop";
import {
  isBrowserPhoneChannelEnabled,
  isCallingEnabled,
  ORG_FEATURES_CHANGED_EVENT,
} from "../../../utils/orgFeatures";
import { fetchAndStoreUserPermissions } from "../../../service/AuthService";
import { useBrowserPhoneContext } from "./BrowserPhoneProvider";
import {
  canUseBrowserPhone,
  hasBrowserPhoneScreenAccess,
  isBrowserPhoneAvailable,
} from "./browserPhoneAccess";
import { requestIncomingCallNotificationPermission } from "./incomingCallAlerts";
import IncomingCallOverlay from "./IncomingCallOverlay";
import GlobalDialer from "./GlobalDialer";
import { BrowserPhoneState } from "./telephonyTypes";

const BrowserPhoneShell = ({ children }) => {
  const { store, actions } = useBrowserPhoneContext();
  const remoteAudioRef = useRef(null);
  const autoConnectAttemptedRef = useRef(false);
  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  const [orgFeaturesVersion, setOrgFeaturesVersion] = useState(0);
  void orgFeaturesVersion;

  const phoneConfig = store.config;
  const browserPhoneEnabled = isBrowserPhoneAvailable(phoneConfig);

  useEffect(() => {
    if (remoteAudioRef.current) {
      actionsRef.current.setRemoteAudioElement(remoteAudioRef.current);
    }
  }, [browserPhoneEnabled]);

  useEffect(() => {
    let cancelled = false;

    const refreshOrgFeatures = async () => {
      const token = getStoredAccessToken();
      if (!token || !isAuthenticated()) {
        return;
      }
      try {
        await fetchAndStoreUserPermissions(token);
        if (!cancelled) {
          setOrgFeaturesVersion((value) => value + 1);
        }
      } catch {
        // Keep cached org features when refresh fails.
      }
    };

    refreshOrgFeatures();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated() || !hasBrowserPhoneScreenAccess()) {
      return undefined;
    }
    actionsRef.current.loadConfig({ forceReload: true }).catch(() => undefined);
    return undefined;
  }, [orgFeaturesVersion]);

  const tryConnectPhone = () => {
    if (!isAuthenticated() || !canUseBrowserPhone() || !browserPhoneEnabled) {
      return;
    }
    actionsRef.current.connect().catch(() => undefined);
  };

  useEffect(() => {
    if (!isAuthenticated() || !canUseBrowserPhone()) {
      return undefined;
    }

    let cancelled = false;

    const bootstrapPhone = async () => {
      try {
        const config = await actionsRef.current.loadConfig();
        if (cancelled || !config?.enableBrowserPhone) {
          return;
        }
        if (autoConnectAttemptedRef.current) {
          return;
        }

        autoConnectAttemptedRef.current = true;
        requestIncomingCallNotificationPermission().catch(() => undefined);
        await actionsRef.current.connect();
      } catch {
        autoConnectAttemptedRef.current = false;
      }
    };

    bootstrapPhone();

    return () => {
      cancelled = true;
    };
  }, [orgFeaturesVersion]);

  useEffect(() => {
    const onOrgFeaturesChanged = () => {
      setOrgFeaturesVersion((value) => value + 1);
      if (!isBrowserPhoneChannelEnabled()) {
        autoConnectAttemptedRef.current = false;
        actionsRef.current.invalidateConfigCache?.();
        actionsRef.current.disconnect?.().catch(() => undefined);
        actionsRef.current.loadConfig({ forceReload: true }).catch(() => undefined);
      }
    };
    window.addEventListener(ORG_FEATURES_CHANGED_EVENT, onOrgFeaturesChanged);
    return () => window.removeEventListener(ORG_FEATURES_CHANGED_EVENT, onOrgFeaturesChanged);
  }, []);

  useEffect(() => {
    if (!browserPhoneEnabled || !isDesktopApp()) {
      return undefined;
    }

    const onVisible = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      if (store.state === BrowserPhoneState.DISCONNECTED) {
        tryConnectPhone();
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [browserPhoneEnabled, store.state]);

  const showPhoneUi = hasBrowserPhoneScreenAccess();

  return (
    <>
      {children}
      {showPhoneUi && browserPhoneEnabled && (
        <audio ref={remoteAudioRef} autoPlay playsInline className="sr-only" aria-hidden="true" />
      )}
      {showPhoneUi && browserPhoneEnabled && <IncomingCallOverlay />}
      {showPhoneUi && <GlobalDialer />}
    </>
  );
};

export default BrowserPhoneShell;
